
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * SecurityServerSettings struct
 */
class SecurityServerSettings {
  /**
   * Initialize struct
   * @param {!number=} securityPort
   * @param {!boolean=} useSsl
   * @param {!number=} slowQueryLogThreshold
   * @constructor
   */
  constructor (securityPort = 6180, useSsl = false, slowQueryLogThreshold = new Int64(100000000, 0)) {
    this.SecurityPort = securityPort
    this.UseSsl = useSsl
    this.SlowQueryLogThreshold = slowQueryLogThreshold
  }

  /**
   * Copy struct (shallow copy)
   * @this {!SecurityServerSettings}
   * @param {!SecurityServerSettings} other Other struct
   * @returns {!SecurityServerSettings} This struct
   */
  copy (other) {
    if (other.SecurityPort != null) {
      this.SecurityPort = other.SecurityPort
    } else {
      this.SecurityPort = null
    }
    if (other.UseSsl != null) {
      this.UseSsl = other.UseSsl
    } else {
      this.UseSsl = null
    }
    if (other.SlowQueryLogThreshold != null) {
      this.SlowQueryLogThreshold = Int64.fromNumber(other.SlowQueryLogThreshold)
    } else {
      this.SlowQueryLogThreshold = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!SecurityServerSettings}
   * @returns {!SecurityServerSettings} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new SecurityServerSettingsModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new SecurityServerSettingsModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!SecurityServerSettings}
   * @param {!SecurityServerSettings} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof SecurityServerSettings)) {
      throw new TypeError('Instance of SecurityServerSettings is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!SecurityServerSettings}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      SecurityPort: ((this.SecurityPort != null) ? this.SecurityPort : null),
      UseSsl: ((this.UseSsl != null) ? this.UseSsl : null),
      SlowQueryLogThreshold: ((this.SlowQueryLogThreshold != null) ? this.SlowQueryLogThreshold.toNumber() : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return SecurityServerSettings.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!SecurityServerSettings} other Object value
   * @returns {!SecurityServerSettings} Created struct
   */
  static fromObject (other) {
    return new SecurityServerSettings().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!SecurityServerSettings}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return SecurityServerSettings.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!SecurityServerSettings}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 216
  }
}

export { SecurityServerSettings };

/**
 * Fast Binary Encoding SecurityServerSettings field model
 */
class FieldModelSecurityServerSettings extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.SecurityPort = new fbe.FieldModelUInt16(buffer, 4 + 4)
    this.UseSsl = new fbe.FieldModelBool(buffer, this.SecurityPort.fbeOffset + this.SecurityPort.fbeSize)
    this.SlowQueryLogThreshold = new fbe.FieldModelInt64(buffer, this.UseSsl.fbeOffset + this.UseSsl.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelSecurityServerSettings}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelSecurityServerSettings}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.SecurityPort.fbeSize + this.UseSsl.fbeSize + this.SlowQueryLogThreshold.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelSecurityServerSettings}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.SecurityPort.fbeExtra + this.UseSsl.fbeExtra + this.SlowQueryLogThreshold.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelSecurityServerSettings}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelSecurityServerSettings.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelSecurityServerSettings}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 216
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelSecurityServerSettings}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelSecurityServerSettings}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.SecurityPort.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.SecurityPort.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SecurityPort.fbeSize

    if ((fbeCurrentSize + this.UseSsl.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.UseSsl.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.UseSsl.fbeSize

    if ((fbeCurrentSize + this.SlowQueryLogThreshold.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.SlowQueryLogThreshold.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SlowQueryLogThreshold.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelSecurityServerSettings}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelSecurityServerSettings}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelSecurityServerSettings}
   * @param {!SecurityServerSettings} fbeValue Default value, defaults is new SecurityServerSettings()
   * @returns {!SecurityServerSettings} SecurityServerSettings value
   */
  get (fbeValue = new SecurityServerSettings()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelSecurityServerSettings}
   * @param {!SecurityServerSettings} fbeValue SecurityServerSettings value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.SecurityPort.fbeSize) <= fbeStructSize) {
      fbeValue.SecurityPort = this.SecurityPort.get(6180)
    } else {
      fbeValue.SecurityPort = 6180
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SecurityPort.fbeSize

    if ((fbeCurrentSize + this.UseSsl.fbeSize) <= fbeStructSize) {
      fbeValue.UseSsl = this.UseSsl.get(false)
    } else {
      fbeValue.UseSsl = false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.UseSsl.fbeSize

    if ((fbeCurrentSize + this.SlowQueryLogThreshold.fbeSize) <= fbeStructSize) {
      fbeValue.SlowQueryLogThreshold = this.SlowQueryLogThreshold.get(new Int64(100000000, 0))
    } else {
      fbeValue.SlowQueryLogThreshold = new Int64(100000000, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SlowQueryLogThreshold.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelSecurityServerSettings}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelSecurityServerSettings}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelSecurityServerSettings}
   * @param {!SecurityServerSettings} fbeValue SecurityServerSettings value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelSecurityServerSettings}
   * @param {SecurityServerSettings} fbeValue SecurityServerSettings value
   */
  setFields (fbeValue) {
    this.SecurityPort.set(fbeValue.SecurityPort)
    this.UseSsl.set(fbeValue.UseSsl)
    this.SlowQueryLogThreshold.set(fbeValue.SlowQueryLogThreshold)
  }
}

export { FieldModelSecurityServerSettings };

/**
 * Fast Binary Encoding SecurityServerSettings model
 */
class SecurityServerSettingsModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelSecurityServerSettings(this.buffer, 4)
  }

  /**
   * Get the SecurityServerSettings model
   * @this {!SecurityServerSettingsModel}
   * @returns {!FieldModelSecurityServerSettings} model SecurityServerSettings model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!SecurityServerSettingsModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!SecurityServerSettingsModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return SecurityServerSettingsModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!SecurityServerSettingsModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelSecurityServerSettings.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!SecurityServerSettingsModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!SecurityServerSettingsModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!SecurityServerSettingsModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!SecurityServerSettingsModel}
   * @param {!SecurityServerSettings} value SecurityServerSettings value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!SecurityServerSettingsModel}
   * @param {!SecurityServerSettings} value SecurityServerSettings value, defaults is new SecurityServerSettings()
   * @return {!object} Deserialized SecurityServerSettings value and its size
   */
  deserialize (value = new SecurityServerSettings()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new SecurityServerSettings(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new SecurityServerSettings(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!SecurityServerSettingsModel}
   * @param {!number} prev Previous SecurityServerSettings model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { SecurityServerSettingsModel };
